import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import {
  type RealtimeMessage,
  RealtimeMessageType,
  type RealtimeEvent,
  RealtimeRole,
} from '../../../features/realtime/realtime.types';
import {
  setFileTransferState,
  setRequestMemberIds,
  setFileTransferSessionId,
  setFilesAvailable,
  resetFileTransferState,
} from '../../../features/fileTransfer/fileTransfer.slice';
import { setFileAvailable } from '../../../features/fileTransfer/thunks/setFileAvailable.thunk';
import { closeModal, openModal } from '../../../features/modals/modals.slice';
import { ModalId } from '../../../features/modals/modals.type';
import {
  FileTransferEventName,
  FileTransferStateType,
  type FileAvailableMessagePayload,
  type RequestMessagePayload,
  type FileTransferEventPayload,
  type DownloadReadyMessagePayload,
} from '../../../features/fileTransfer/fileTransfer.type';
import { store } from '../../../stores/redux';
import { sendFileTransferRealtimeMessage } from './sendFileTransferRealtimeMessage';
import { getIdentity } from '../../../features/realtime/realtime.selectors';
import { getLogger } from '../../../libs/logger';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { subscribeToCallState } from './fileTransferToCallState';
import { dismissAllNotifications } from './useFileTransferNotifications';

let isMounted = false;
const logger = getLogger('listenForFileTransfer');

const DELAY_TO_ALLOW_NOTIFICATION = 1000;

function onFileTransferRequest(
  message: RealtimeMessage,
  event: RealtimeEvent,
): void {
  const { eventName, eventData } = message.payload as FileTransferEventPayload;
  const state = store.getState();
  const realtimeIdentity = getIdentity(state);
  const { role } = getApolloSessionDetails(state);

  // Log sanitized data - exclude sensitive fields entirely
  const sanitizedState = {
    currentState: state.filetransfer.currentState,
    requestMemberIds: state.filetransfer.requestMemberIds,
    uploadingFilesCount: state.filetransfer.uploadingFilesCount,
    // Exclude filesAvailable and filesUploaded for security
  };

  const sanitizedEvent = {
    from: event.from ? { id: event.from.id } : undefined,
    // Exclude other potentially sensitive event data
  };

  logger.log('[fileTransfer/eventReceived]', {
    type: eventName,
    role: realtimeIdentity?.role,
    sessionId: state.filetransfer.fileTransferSessionId,
    state: sanitizedState,
    event: sanitizedEvent,
  });

  switch (eventName) {
    case FileTransferEventName.Request: {
      const { fileTransferSessionId } = eventData as RequestMessagePayload;
      store.dispatch(openModal(ModalId.FileTransfer));
      store.dispatch(setFileTransferSessionId(fileTransferSessionId));
      store.dispatch(setRequestMemberIds([event.from.id]));
      store.dispatch(
        setFileTransferState(FileTransferStateType.RequestReceived),
      );
      break;
    }
    case FileTransferEventName.FileAvailable: {
      const { realtimeMemberId, fileReady, fileUuid, uploadStatus } =
        eventData as FileAvailableMessagePayload;

      if (realtimeMemberId === undefined) {
        throw new Error(
          'realtimeMemberId is undefined on FileTransferEventName.FileAvailable event',
        );
      }

      // Checking that who receiverd this message is actually the user that uploaded the file
      const isUploader = realtimeIdentity?.id === realtimeMemberId;

      if (isUploader) {
        if (uploadStatus === 'failed') {
          void store.dispatch(openModal(ModalId.FileTransfer));
          void store.dispatch(
            setFileTransferState(FileTransferStateType.ServerError),
          );
        } else {
          // Adds the new file to the available ones
          void store.dispatch(setFileAvailable(fileReady));
        }
      } else {
        // Proxying the message from provider to patient
        void sendFileTransferRealtimeMessage(
          [realtimeMemberId],
          FileTransferEventName.FileAvailable,
          { fileReady, realtimeMemberId, fileUuid, uploadStatus },
        );
      }
      break;
    }
    // We are ready to download the files
    case FileTransferEventName.DownloadReady: {
      const { filesReady } = eventData as DownloadReadyMessagePayload;
      store.dispatch(openModal(ModalId.FileTransfer));
      void store.dispatch(setRequestMemberIds([event.from.id]));
      void store.dispatch(setFilesAvailable(filesReady));
      store.dispatch(
        setFileTransferState(FileTransferStateType.FilesReadyForDownload),
      );
      break;
    }
    // The receiver is currently downloading our files
    case FileTransferEventName.DownloadInProgress: {
      if (
        Object.keys(state.filetransfer.filesAvailable).length === 0 ||
        state.filetransfer.currentState ===
          FileTransferStateType.RemoteDownloadCompleted
      ) {
        // Avoids possible issues with sending a DownloadInProgress message after DownloadCompleted. Especially since DownloadCompleted resets the state only after a delay.
        return;
      }
      store.dispatch(
        setFileTransferState(FileTransferStateType.RemoteDownloading),
      );
      break;
    }
    // The receiver has finished downloading our files
    case FileTransferEventName.DownloadCompleted: {
      store.dispatch(
        setFileTransferState(FileTransferStateType.RemoteDownloadCompleted),
      );
      setTimeout(
        () => store.dispatch(resetFileTransferState()),
        DELAY_TO_ALLOW_NOTIFICATION,
      );
      break;
    }
    // Patient has rejected the request to send files
    case FileTransferEventName.RejectRequest: {
      store.dispatch(
        setFileTransferState(FileTransferStateType.RequestRejected),
      );
      store.dispatch(openModal(ModalId.FileTransfer));

      break;
    }
    // Receiver has cancelled the download
    case FileTransferEventName.DownloadCancelled: {
      store.dispatch(
        setFileTransferState(FileTransferStateType.DownloadCancelled),
      );

      if (role === RealtimeRole.Provider) {
        store.dispatch(openModal(ModalId.FileTransfer));
      } else {
        setTimeout(
          () => store.dispatch(resetFileTransferState()),
          DELAY_TO_ALLOW_NOTIFICATION,
        );
      }
      break;
    }
    case FileTransferEventName.UploadCancelled: {
      void dismissAllNotifications();
      if (role === RealtimeRole.Provider) {
        store.dispatch(
          setFileTransferState(FileTransferStateType.RemoteUploadCancelled),
        );
        store.dispatch(openModal(ModalId.FileTransfer));
      }
      break;
    }
    case FileTransferEventName.DownloadStoppedRemotely: {
      store.dispatch(
        setFileTransferState(FileTransferStateType.RemoteDownloadCancelled),
      );
      store.dispatch(closeModal(ModalId.FileTransfer));
      setTimeout(
        () => store.dispatch(resetFileTransferState()),
        DELAY_TO_ALLOW_NOTIFICATION,
      );
      break;
    }

    default:
  }
}

export const listenForFileTransfer = (): void => {
  if (isMounted) {
    return;
  }
  isMounted = true;

  subscribeToCallState();

  subscribeToMessage({
    messageType: RealtimeMessageType.FileTransferRequest,
    callback: onFileTransferRequest,
  });
};
